import React, { useState, useEffect, useRef } from 'react';
import { hot } from 'react-hot-loader';
import { connect } from 'react-redux';
import Link from 'redux-first-router-link';
import selectAnalytics from './selectAnalytics';
import cartAnalytics from '../Selection/cartAnalytics';
import ExternalVideo from '../Product/ExternalVideo';
import Price from '../Product/Price';
import NotifyMe from '../Header/NotifyMe';
import Button from '../Elements/Button/Button';
import LazyImage from '../../hocs/Lazy/LazyImage';
import Slider from '../../hocs/Slider/Slider';
import { addToBag, updateListItemCache } from '../../actions/checkout';
import { searchInput } from '../../actions/environment';
import { addClass, removeClass } from '../../utils/dom';
import { imageSizeTranslation } from '../../utils/image';
import { translate, prefix, translateObjectData } from '../../utils/locale';
import { urlify, urlifyAllowAccents, createColors, splitNewLine } from '../../utils/string';
import s from './ProductItem.scss';

let productContainerEl = null;

const ProductItem = ({ variantClasses, content, setup, currentPath, currentHash, currentView, canonical, searchInputText, imageSize, layout, listItemCache, productCache = {}, variation = '', itemIndex = 1, ...props }) => {
	const { products: cache, productVariantLookup } = productCache;
	content = translateObjectData(setup, content);

	// Button text
	let buttonStartText = 'addToBag';
	if (content.inStock === 'infinite') buttonStartText = 'preOrderNow';
	if (!content.inStock && !content.limitedEdition) buttonStartText = 'notifyMe';
	const [buttonState, updateButton] = useState(buttonStartText);
	const [notifyMeOpen, toggleNotifyMe] = useState(false);
	const productContainer = useRef(null);

	const path = canonical ? translate(setup, content, 'canonicalUri') : `${currentPath}/${content.uri}${currentHash}`;
	let productItemClasses = [s.productItem, 'u_productItem'];
	if (variantClasses) productItemClasses = [...productItemClasses, ...(Array.isArray(variantClasses) ? variantClasses.filter((c) => !!c && c.indexOf('productItem') !== -1).map((c) => s[c]) : [s[variantClasses.toString()]])];
	const price = content.price[setup.currency];

	const colors = createColors(content.color);

	if (content.productHidden) return null;

	if (!content.inStock) productItemClasses.push(s.productItem__soldOut);
	if (price.compareAtPrice && price.compareAtPrice !== price.price) productItemClasses.push(s.productItem__sale);
	if (content.tags && content.tags.indexOf('new') !== -1) productItemClasses.push(s.productItem__new);
	if (variation) productItemClasses.push(s[`productItem__${variation}`]);

	const badges = [];

	if (content.inStock === 'infinite') {
		badges.push(<span key="coming-soon-badge" className={s.productItem_badge}>{ translate(setup, 'comingSoon') }</span>);
	} else if (content.inStock > 0 && content.inStock < 4) {
		badges.push(<span key="low-in-stock-badge" className={s.productItem_badge}>{ translate(setup, 'lowInStock') }</span>);
	}

	if (content.badges) {
		splitNewLine(content.badges).forEach((badge) => badges.push(<span key={urlify(badge)} className={s.productItem_badge}>{ badge }</span>));
	}

	const clearSearch = () => {
		if (searchInputText !== '') props.searchInput('');
		props.closeSearch();
		selectAnalytics(content, setup, currentView, itemIndex);
		props.updateListItemCache(content.variantId, currentView);
	};

	const hoverHandler = (e) => {
		if (e.type === 'touchstart' && productContainer?.current) {
			if (productContainerEl) removeClass(productContainerEl, 'u_hovering');
			productContainerEl = productContainer?.current;
			addClass(productContainer.current, 'u_hovering');
		}
	};

	const toggleNotifyMeHandler = (isOpen) => {
		if (isOpen) {
			toggleNotifyMe(true);
		} else {
			toggleNotifyMe('exiting');
			setTimeout(() => {
				toggleNotifyMe(false);
			}, 350);
		}
	};

	const addToBagHandler = (e) => {
		e.preventDefault();
		if (buttonStartText === 'notifyMe') {
			toggleNotifyMe(true);
			return;
		}
		const currentCategory = currentPath.slice(0, currentPath.lastIndexOf('/'));
		const isBundle = content.bundle;
		let cachedItems = [content];
		let selected = [content.sizes[0].id];

		if (isBundle) {
			cachedItems = [];
			selected = [];
			splitNewLine(isBundle).filter((item) => cache[productVariantLookup[item]]).forEach((item) => {
				const cachedItem = cache[productVariantLookup[item]];
				cachedItems = [...cachedItems, cachedItem];
				selected = [...selected, `gid://shopify/ProductVariant/${item}`];
				props.updateListItemCache(cachedItem.variantId, currentView);
			});
		} else {
			props.updateListItemCache(content.variantId, currentView);
		}

		updateButton('itemBeingAdded');

		const lineItems = [];

		selected.forEach((selectedItem) => {
			lineItems.push({
				variantId: selectedItem,
				quantity: 1,
			});
		});

		props.addToBag(lineItems, cachedItems, currentCategory).then((response) => {
			updateButton('itemAdded');

			cartAnalytics(lineItems, cache, productVariantLookup, setup, currentCategory, response.data, currentView);

			window.setTimeout(() => {
				addClass(document.body, 'u_addedToBag');

				window.setTimeout(() => {
					updateButton(buttonStartText);
				}, 2000);

				window.setTimeout(() => {
					removeClass(document.body, 'u_addedToBag');
				}, 4000);
			}, 500);
			return response;
		}).catch(() => {
			updateButton('itemAddedError');

			window.setTimeout(() => {
				updateButton('quickShop');
			}, 2000);
		});
	};

	useEffect(() => () => {
		removeClass(document.body, 'u_addedToBag');
	});

	return (
		<div className={productItemClasses.join(' ')} data-product-id={content.variantId} data-product-index={itemIndex} ref={productContainer}>
			<Link className={s.productItem_link} to={prefix(setup, path)} onClick={clearSearch} onTouchStart={hoverHandler} onTouchEnd={hoverHandler}>
				<div className={s.productItem_badges}>
					{ badges }
				</div>
				<div className={[s.productItem_images, (layout !== 'single' ? s.productItem_images__default : null)].join(' ')}>
					{
						layout === 'single' && content?.media && (
							<Slider adaption="product" infinite>
								{
									content.media.map((item) => {
										if (!item || !item.src) return null;
										return (
											<figure key={item.src} className={s.productItem_image}>
												{ item.type === 'IMAGE' && <LazyImage {...imageSizeTranslation(4, item, '_')} /> }
												{ item.type === 'EXTERNAL_VIDEO' && <ExternalVideo url={item.src} uid={urlifyAllowAccents(item.alt)} itemProp="thumbnail" /> }
											</figure>
										);
									})
								}
							</Slider>
						)
					}
					{
						layout !== 'single' && content?.media && content.media.slice(0, 2).map((item) => {
							if (!item || !item.src) return null;
							return (
								<figure key={item.src} className={s.productItem_image}>
									{ item.type === 'IMAGE' && <LazyImage {...imageSizeTranslation(4, item, '_')} /> }
									{ item.type === 'EXTERNAL_VIDEO' && <ExternalVideo url={item.src} uid={urlifyAllowAccents(item.alt)} itemProp="thumbnail" /> }
								</figure>
							);
						})
					}
				</div>
				<div className={s.productItem_info}>
					<div className={s.productItem_info_columns}>
						<div className={s.productItem_info_columns_column}>
							<span className={s.productItem_info_name}>{ content.name }<br />{ content.variant }<br />{ content.finish }<br />{ content.weight }</span>
						</div>
						<div className={[s.productItem_info_colors, (colors ? s[`productItem_info_colors__${colors.length}`] : null)].join(' ')}>
							{
								colors.map(([hex]) => (
									<span key={hex} className={s.productItem_info_color} style={{ backgroundColor: hex || '#000' }} />
								))
							}
						</div>
						<div className={s.productItem_info_price}>
							<Price price={price} setup={setup} soldOut={!content.inStock ? translate(setup, 'soldOut') : false} />
						</div>
					</div>
				</div>
			</Link>
			{
				content.sizes.length < 2 && (
					<form className={s.productItem_quickShop} action="POST" onSubmit={addToBagHandler}>
						<Button variantClasses={['button__text', 'button__quickShop']} type={(content.inStock || (!content.inStock && !content.limitedEdition)) ? 'submit' : 'link'} location={prefix(setup, path)}><span>{ translate(setup, buttonState) }</span></Button>
					</form>
				)
			}
			{
				notifyMeOpen !== false && <NotifyMe cachedProduct={content} notifyMeState={notifyMeOpen} toggleNotifyMe={toggleNotifyMeHandler} />
			}
		</div>
	);
};

const mapState = ({ environment, productCache, checkout }, props) => ({
	setup: environment.setup,
	searchInputText: environment.searchInputText,
	productCache: productCache?.[environment.setup.market]?.[environment.setup.pricelist],
	listItemCache: checkout.listItemCache,
	...props,
});

export default hot(module)(connect(mapState, { searchInput, addToBag, updateListItemCache })(ProductItem));
